import rootStore from "@/app/store";
import crud, { ApiResponse } from "../crud";
import { BillingDepositState, BillingType } from "../billing";

export enum ContractStatus {
  MORE_THAN_MONTH = 3,
  MORE_THAN_WEEK = 2,
  LESS_THAN_WEEK = 1,
}

export interface RentPlan {
  office_id: number;
  office_name: string;
  price: number;
  deposit: {
    amount: number;
    state: BillingDepositState;
  };
  rent_id: number | null;
  renter_id: number | null;
  renter_name: string | null;
  workplace_count: number;
  office_floor: number;
  contract_status: ContractStatus;
  location_name: string;
  location_id: string;
  price_per_workplace: number;
  potential_earning_price: number;
  potential_workspace_earning_price: number;
  price_per_sqm: number;
  potential_price_per_sqm: number;
}

export interface ActualPayment {
  office_id: number;
  office_name: string;
  total_paid: number | null;
  renter_id: number;
  renter_name: string;
  billing_id: number;
  billing_type: BillingType;
  billing_number: string;
  paid: boolean;
  agreement_id: number;
}

export interface PotentialEarning {
  location_id: number;
  location_name: string;
  potential_earnings: number;
}

export interface DebtSummary {
  office_id: number;
  office_name: string;
  debt: number;
}

export type SummaryRow = {
  deposit: number;
  payment_actual: number;
  payment_plan: number;
  workplaces: number;
  potential_earnings: number;
  potential_price_per_sqm: number;
  potential_price_per_workplace: number;
  price_per_sqm: number;
  price_per_workplace: number;
  total_square_meters: number;
};

export interface SummaryReport {
  rent_plan: RentPlan[];
  actual_payments: ActualPayment[];
  potential_earnings: PotentialEarning[];
  debt_summary: DebtSummary[];
  summary: SummaryRow;
}

export interface OfficeEstimate {
  office_id: number;
  location_id: number;
  area_sqm: number;
  building_class: string | null;
  pct_median: number;
  pct_iqr: number;
  estimate_low_monthly_rur: number | null;
  estimate_monthly_rur: number | null;
  estimate_high_monthly_rur: number | null;
  source_breakdown: {
    source: string;
    included: boolean;
    n?: number;
    low_monthly_for_subject?: number | null;
    median_monthly_for_subject?: number | null;
    high_monthly_for_subject?: number | null;
    confidence?: string;
    note?: string;
  }[];
  combined_method: string;
  data_quality: {
    n_pool_rows: number;
    n_cohort_rows: number;
    n_market_rates: number;
    history_window_start: string;
    notes: string[];
  };
  computed_at: string;
}

export interface HistoryStep {
  step_date: string;
  percentile: number | null;
  fact_rur_m2_month: number | null;
  n_market: number;
  n_deals: number;
  market_rate_p10: number | null;
  market_rate_p50: number | null;
  market_rate_p90: number | null;
  estimate_low_rur_m2_month: number | null;
  estimate_rur_m2_month: number | null;
  estimate_high_rur_m2_month: number | null;
}

export interface EstimateHistory {
  location_id: number;
  office_id: number | null;
  steps: HistoryStep[];
}

export function AnalyticsAPI(baseUrl: string) {
  return {
    summary: async (
      period: string,
      year: string = "2024",
      location: number,
      types = "",
    ) => {
      const businessProfileId =
        rootStore.getState().businessProfile.businessProfile.id;
      return (await crud.get(
        `${baseUrl}/analytics/summary?business_profile_id=${businessProfileId}&period=${period}&year=${year}&location_id=${location}&types=${types}`,
        undefined,
        true,
      )) as ApiResponse<SummaryReport>;
    },
    getEstimate: async (officeId: number) => {
      return (await crud.get(
        `${baseUrl}/analytics/estimate?office_id=${officeId}`,
        undefined,
        true,
      )) as { estimates: OfficeEstimate[] };
    },
    getHistory: async (
      locationId: number,
      dateFrom: string,
      dateTo: string,
      officeId?: number,
    ) => {
      let url = `${baseUrl}/analytics/estimate/history?location_id=${locationId}&date_from=${dateFrom}&date_to=${dateTo}`;
      if (officeId) url += `&office_id=${officeId}`;
      return (await crud.get(url, undefined, true)) as EstimateHistory;
    },
  };
}
